import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { sha256 } from "js-sha256";

import {
  Input,
  Select,
  Label,
  Button,
  Message,
  Icon,
  Checkbox,
} from "../../common";
import { FormErrorMessage } from "../../messages";
import { ReactComponent as AttentionImg } from "../../../assets/illustrations/attention-96x96.svg";
import {
  ACCOUNT_TYPE_STRING,
  CHAINS,
  CHAINS_MAP,
  SUPPORT_EMAIL,
} from "../../../constants";
import { selectCurrentUser } from "../../../store/reducers/user";
import { useAddWalletMutation } from "../../../store/services/wallets";
import { UIStatus } from "../../../types/GlobalTypes";
import { BaseWallet, Chain, ChainType } from "../../../types/WalletTypes";
import { isMobileMedia } from "../../../utils/media";
import { getBasePath } from "../../../utils/router";
import { track } from "../../../utils/tracking";

type Props = {
  callback: (data?: any) => void;
};

type Inputs = {
  walletCorrect: boolean;
  walletSupportsCoin: boolean;
  personResponsible: boolean;
} & BaseWallet;

const getAddressTexts = (chainId?: Chain) => {
  if (!chainId) {
    return { label: "Wallet address", hint: undefined };
  }

  const chainDetails = CHAINS_MAP[chainId];

  if (!chainDetails) {
    return { label: "Wallet address", hint: undefined };
  }

  const { name, tokenStandard } = chainDetails;

  return {
    label: `${name} wallet address`,
    hint: tokenStandard
      ? `We send stablecoins (${tokenStandard}) on ${name} network`
      : undefined,
  };
};

export const NewWalletForm: FC<Props> = ({ callback }) => {
  const { account_type, allowed_outgoing_chains, ga_id } =
    useSelector(selectCurrentUser);

  const [checkedDisclaimersCount, setCheckedDisclaimersCount] = useState(0);
  const location = useLocation();

  const allAllowedChains = useMemo(
    () => Object.values(allowed_outgoing_chains || []).flat(),
    [allowed_outgoing_chains]
  );

  const basePath = useMemo(
    () => getBasePath(location.pathname),
    [location.pathname]
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
    watch,
  } = useForm<Inputs>({
    defaultValues: {
      name: "",
      chain: undefined,
      address: "",
      walletCorrect: false,
      walletSupportsCoin: false,
      personResponsible: false,
    },
  });

  const selectedChain = watch("chain");

  const allowedChains = useMemo(
    () =>
      CHAINS.filter(
        ({ value, type }) =>
          type === ChainType.CRYPTO && allAllowedChains.includes(value)
      ),
    [allAllowedChains]
  );

  const chainOptions = useMemo(
    () => allowedChains.map(({ value, name }) => ({ value, label: name })),
    [allowedChains]
  );

  const { label: addressLabel, hint: addressHint } = useMemo(
    () => getAddressTexts(selectedChain),
    [selectedChain]
  );

  const trackWallet = useCallback(
    (event: string, props?: Record<string, string | undefined>) => {
      track(event, {
        account_type: ACCOUNT_TYPE_STRING[account_type],
        user_id: ga_id,
        uid: ga_id,
        caller: basePath,
        ...props,
      });
    },
    [account_type, basePath, ga_id]
  );

  const trackWalletField = useCallback(
    (name: string, value?: string) => {
      if (value) {
        trackWallet(`wallet_${name}_added`, {
          [name]: (() => {
            switch (name) {
              case "chain":
                return value;
              case "address":
                return sha256(value);
              default:
                return undefined;
            }
          })(),
        });
      }
    },
    [trackWallet]
  );

  const trackDisclaimers = useCallback((checked: boolean) => {
    setCheckedDisclaimersCount((curr) => (checked ? curr + 1 : curr - 1));
  }, []);

  useEffect(() => trackWallet("wallet_creation_initiated"), [trackWallet]);

  useEffect(() => {
    if (checkedDisclaimersCount === 3) {
      trackWallet("wallet_disclaimers_checked");
    }
  }, [checkedDisclaimersCount, trackWallet]);

  const [addWallet, { data, isSuccess, isError, error }] =
    useAddWalletMutation();

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data) => {
      try {
        await addWallet({
          name: data.name,
          address: data.address,
          chain: data.chain,
        });

        trackWallet("wallet_created", {
          chain: data.chain,
          address: sha256(data.address!),
        });
      } catch (error) {
        throw error;
      }
    },
    [addWallet, trackWallet]
  );

  useEffect(() => {
    if (isSuccess && callback) {
      callback(data);
    }
  }, [isSuccess, callback, data]);

  return (
    <form
      className="flex flex-col gap-6 md:gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Message
        type={UIStatus.WARNING}
        text="Make sure you selected the correct network and wallet address. Archway is not responsible for funds sent to wrong wallets."
        heading="Full focus"
        img={isMobileMedia() ? undefined : AttentionImg}
      />

      <Label label="Wallet name">
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              placeholder="Come up with a wallet name"
              autoFocus
              {...field}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value);
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <hr className="hidden md:block border-gray-50" />

      <Label label="Chain">
        <Controller
          name="chain"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select a chain"
              options={chainOptions}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value || "");
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <Label label={addressLabel}>
        <Controller
          name="address"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              {...field}
              note={addressHint}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value);
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <Message
        areaClassName="pt-4 px-6 pb-6"
        type={UIStatus.NEUTRAL}
        text="If you want to make sure the wallet is correct, you can verify your wallet with our support service."
      >
        <Button component={NavLink} to={`mailto:${SUPPORT_EMAIL}`}>
          <span className="capitalize font-semibold text-base flex flex-row gap-x-3">
            Contact support{" "}
            <Icon icon="Send" size={24} className="text-gray-500" disableFill />
          </span>
        </Button>
      </Message>

      <div className="flex flex-col gap-y-4">
        <Controller
          name="walletCorrect"
          control={control}
          rules={{ required: true }}
          render={({ field, field: { value } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                trackDisclaimers(e.target.checked);
              }}
            >
              <span className="text-gray-300">
                I am sure the wallet address is correct
              </span>
            </Checkbox>
          )}
        />

        <Controller
          name="walletSupportsCoin"
          control={control}
          rules={{ required: true }}
          render={({ field, field: { value } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                trackDisclaimers(e.target.checked);
              }}
            >
              <span className="text-gray-300">
                I am sure the wallet address supports the selected coin
              </span>
            </Checkbox>
          )}
        />

        <Controller
          name="personResponsible"
          control={control}
          rules={{ required: true }}
          render={({ field, field: { value } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                trackDisclaimers(e.target.checked);
              }}
            >
              <span className="text-gray-300">
                I understand I am the only person responsible for the wallet
                information. If it’s wrong I’ll permanently lose my funds
              </span>
            </Checkbox>
          )}
        />
      </div>

      <FormErrorMessage className="mb-6" isError={isError} error={error} />

      <div className="flex flex-col items-center pb-0 h-18 !z-[50] bg-white">
        <Button
          block
          type="submit"
          variant={Button.variant.PRIMARY}
          disabled={!isDirty || !isValid}
        >
          Add wallet
        </Button>
      </div>
    </form>
  );
};
